import type { AppDispatch, RootState, ThunkApi } from "@src/store/rootState";
import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { getAiChat } from "@src/store/slices/aiChat";
import { hasBuilderChanges } from "@src/store/slices/experienceBuilder/common/selectors/hasBuilderChanges";
import {
  trackAllModsSaved,
  trackCloseCompleted,
  trackCloseStarted,
  trackDiscardModalClosed,
  trackDiscardModalViewed,
  trackOnsiteUpsellCreationCompleted,
} from "@src/store/slices/experienceBuilder/common/thunks/mixPanel";
import { notifyBuilderEvent } from "@src/store/slices/experienceBuilder/common/thunks/notifyBuilderEvent";
import { confirm } from "@src/store/slices/experienceBuilder/common/utils/confirm";
import { saveMods } from "@src/store/slices/experienceBuilder/common/utils/saveMods";
import { closeDialog } from "@src/store/slices/global";
import {
  ExperienceBuilderAiAction,
  ExperienceBuilderAiSelector,
} from "@src/store/slices/experienceBuilder/ai";

const saveModsAndTrack = (dispatch: AppDispatch, getState: () => RootState) => {
  saveMods(dispatch, getState);

  void dispatch(trackAllModsSaved());
  void dispatch(trackOnsiteUpsellCreationCompleted());
  void dispatch(trackDiscardModalClosed({ choice: "save_and_exit" }));
  void dispatch(trackCloseCompleted({ close_modal_choice: "save" }));
  void dispatch(notifyBuilderEvent("saved"));
};

export const closeBuilder = createAsyncThunk<boolean, void>(
  "experienceBuilder/closeBuilder",
  async (_, api) => {
    // Snapshot BEFORE closing: `close()` (on confirm) and the builder-close
    // lifecycle listener both end up wiping `state.aiChat`, so anything we
    // want to hand back to the creation chat has to be captured now.
    const hasCreationOrigin =
      !!ExperienceBuilderAiSelector.getOriginExperienceCreationSessionId(
        api.getState()
      );
    const vivChat = hasCreationOrigin ? getAiChat(api.getState()) : undefined;

    const isClosed = await close(api);
    if (isClosed) {
      api.dispatch(ExperienceBuilderAiAction.stopVivChat("close_builder"));
      if (vivChat) {
        api.dispatch(
          ExperienceBuilderAiAction.seedCreationHandoffOnExit(vivChat)
        );
      }
    }
    return isClosed;
  }
);

async function close({ dispatch, getState }: ThunkApi) {
  void dispatch(trackCloseStarted());

  const hasChanges = hasBuilderChanges(getState());
  if (!hasChanges) {
    dispatch(closeDialog("experienceBuilder"));
    void dispatch(trackCloseCompleted());
    return true;
  }

  void dispatch(trackDiscardModalViewed({ source: "close_builder" }));

  const result = await confirm(dispatch, {
    title: "You have unsaved modifications",
    message: "Save before exiting or your modifications will be lost.",
    confirmText: "Save modifications",
    cancelText: "Discard",
    showCloseIcon: true,
  });

  switch (result) {
    case "confirm":
      saveModsAndTrack(dispatch, getState);
      dispatch(closeDialog("experienceBuilder"));
      return true;
    case "cancel":
      void dispatch(trackDiscardModalClosed({ choice: "discard_and_exit" }));
      void dispatch(trackCloseCompleted({ close_modal_choice: "discard" }));
      void dispatch(notifyBuilderEvent("discarded"));
      dispatch(closeDialog("experienceBuilder"));
      return true;
    case "close":
      void dispatch(trackDiscardModalClosed({ choice: "keep_editing" }));
      return false;
  }
}
